import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Layout,
  Flex,
  Dropdown,
  Button,
  Tabs,
  Spin,
  Empty,
  message,
} from "antd";
import {
  MoreOutlined,
  LoadingOutlined
} from "@ant-design/icons";
import {
  CloneIcon,
  EditIcon,
  KebabMenuIcon,
  TrashIcon,
  ViewDetailsIcon,
} from "../../assets/CustomIcon";

import PageLayout from "../../components/common/Layouts/PageLayout";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import ImageCardDisplay from "../../components/common/ImageCardDisplay";
import {
  fetchVenues,
  selectVenues,
  selectFetchVenuesLoading,
  softDeleteVenue,
  selectSoftDeletedVenues,
  deleteVenue,
} from "../../redux/venueSlice";

const { Content } = Layout;

const MAX_DESCRIPTION_WORDS = 10;

const getVenueDescription = (description) => {
  if (!description) {
    return "No description";
  }

  const words = description.trim().split(/\s+/);
  if (words.length <= MAX_DESCRIPTION_WORDS) {
    return description;
  }

  return `${words.slice(0, MAX_DESCRIPTION_WORDS).join(" ")}...`;
};

export default function VenuesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get venues from Redux store
  const venues = useSelector(selectVenues);
  const softDeletedVenues = useSelector(selectSoftDeletedVenues);
  const loading = useSelector(selectFetchVenuesLoading);

  // Handle soft delete venue
  const handleSoftDeleteVenue = (venueId, venueName) => {
    console.log('Soft deleting venue:', venueId, venueName);
    // TODO: Uncomment below line when backend gives delete permission
    dispatch(deleteVenue(venueId));
    dispatch(softDeleteVenue(venueId));
    message.success(`"${venueName}" moved to Deleted`);
  };

  // Fetch venues on component mount
  useEffect(() => {
    dispatch(fetchVenues());
  }, [dispatch]);

  // CHANGE: Updated to accept full venue object for navigation state
  const getMenuItems = (venue) => [
    {
      key: "view",
      icon: (
        <span className="dropdownItemIcon">
          <ViewDetailsIcon />
        </span>
      ),
      label: "View Details",
      // CHANGE: Navigate with full venue object
      onClick: () => navigate(`/venue-details/${venue.id}`, { state: { venue: venue } }),
    },
    {
      key: "edit",
      icon: (
        <span className="dropdownItemIcon">
          <EditIcon />
        </span>
      ),
      label: "Edit",
      onClick: () => navigate(`/edit-venue/${venue.id}`),
    },
    {
      key: "duplicate",
      icon: (
        <span className="dropdownItemIcon">
          <CloneIcon />
        </span>
      ),
      label: "Duplicate",
      onClick: () => console.log("Duplicate", venue.id),
    },
    {
      key: "delete",
      icon: (
        <span className="dropdownItemIcon">
          <TrashIcon />
        </span>
      ),
      className: "deleteMenuItem",
      label: "Delete",
      danger: true,
      onClick: () => handleSoftDeleteVenue(venue.id, venue.name),
    },
  ];

  const goToCreateNewVenuePages = () => {
    navigate("/create-new-venue");
  };

  // Loading spinner
  const antIcon = <LoadingOutlined style={{ fontSize: 48 }} spin />;

  // Render venue cards
  const renderVenueCards = (venuesList) => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin indicator={antIcon} tip="Loading venues..." />
        </div>
      );
    }

    if (!venuesList || venuesList.length === 0) {
      return (
        <Empty
          description="No venues found. Create your first venue!"
          style={{ padding: '50px' }}
        />
      );
    }

    return (
      <Row gutter={[20, 20]}>
        {venuesList.map((venue, index) => (
          <Col xs={24} sm={24} md={12} lg={12} key={venue.id || `venue-${index}`}>
            <Card hoverable className="eventCard">
              <Dropdown
                key={venue.id}
                menu={{ items: getMenuItems(venue) }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button
                  icon={<MoreOutlined />}
                  size="small"
                  type="text"
                  className="eventActionMenu noStyleBtn"
                />
              </Dropdown>

              <div className="eventHeader">
                <div className="eventImgBlk">
                  <ImageCardDisplay cardElement={venue} isCard={true} />
                </div>
                <Flex
                  vertical
                  gap={8}
                  className="eventInfo"
                  justify="center"
                >
                  <Flex vertical gap={8}>
                    <h3 className="eventTitle">{venue.name}</h3>
                    <Flex vertical>
                      <p className="eventLocation">
                        {getVenueDescription(venue.description)}
                      </p>
                      <p className="eventAddress">
                        {venue.address_line1 || 'Address not available'}
                        {venue.address_line2 && `, ${venue.address_line2}`}
                        {venue.landmark && ` (Near: ${venue.landmark})`}
                      </p>
                    </Flex>
                    <div className="eventDates">
                      <div className="dateItem">
                        <span className="dateLabel">
                          Capacity
                        </span>
                        <span className="dateValue">
                          {venue.capacity || 'N/A'}
                        </span>
                      </div>
                      <div className="dateItem">
                        <span className="dateLabel">Tags</span>
                        <span className="dateValue">
                          {venue.tags || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </Flex>
                </Flex>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  // Render deleted venue cards with restore option
  const renderDeletedVenueCards = (venuesList) => {
    if (!venuesList || venuesList.length === 0) {
      return (
        <Empty
          description="No deleted venues. Deleted venues will appear here."
          style={{ padding: '50px' }}
        />
      );
    }
    return (
      <Row gutter={[20, 20]}>
        {venuesList.map((venue) => (
          <Col xs={24} sm={24} md={12} lg={12} key={venue.id}>
            <Card hoverable className="eventCard">
              <Dropdown
                menu={{ items: getMenuItems(venue.id) }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button
                  size="small"
                  icon={<KebabMenuIcon />}
                  className="eventActionMenu noStyleBtn"
                />
              </Dropdown>

              <div className="eventHeader">
                <div className="eventImgBlk">
                  <ImageCardDisplay cardElement={venue} isCard={true} />
                </div>
                <Flex
                  vertical
                  gap={8}
                  className="eventInfo"
                  justify="center"
                >
                  <Flex vertical gap={8}>
                    <h3 className="eventTitle">{venue.name}</h3>
                    <Flex vertical>
                      <p className="eventLocation">
                        {venue.eventSpace}
                      </p>
                      <p className="eventAddress">
                        {venue.address}
                      </p>
                    </Flex>
                    <div className="eventDates">
                      <div className="dateItem">
                        <span className="dateLabel">
                          Capacity
                        </span>
                        <span className="dateValue">
                          {venue.capacity}
                        </span>
                      </div>
                      <div className="dateItem">
                        <span className="dateLabel">Manager</span>
                        <span className="dateValue">
                          {venue.manager}
                        </span>
                      </div>
                    </div>
                  </Flex>
                </Flex>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  // Filter venues by status for different tabs - ensure it's always an array
  const allVenues = Array.isArray(venues) ? venues : [];
  const draftVenues = allVenues.filter(v => v.is_drafted === true);
  const openVenues = allVenues.filter(v => v.is_active === true && !v.is_drafted);
  const closedVenues = allVenues.filter(v => v.is_active === false);

  return (
    <PageLayout title="My Venues">
      {/* SEO HELMET WRAPPER */}
      <HelmetWrapper
          title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai" 
          description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability, 
          and city-specific visibility for organizers and users." 
          keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata, 
          online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai, 
          Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai, 
          Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
      />
      <Content className="contentWrap">
        <div className="relative">
          <div className="createBtnBlk">
            <Button
              type="primary"
              size="large"
              className="btnGradient publishBtn"
              onClick={goToCreateNewVenuePages}
            >
              Create Venue
            </Button>
          </div>
          <Tabs
            defaultActiveKey="all"
            className="eventTabs"
            type="card"
            items={[
              {
                key: "all",
                label: "All Venues",
                children: (
                  <div className="tabContentWrap">
                    {renderVenueCards(allVenues)}
                  </div>
                ),
              },
              {
                key: "calendar",
                label: "Calendar",
                children: <div className="tabContentWrap">Calendar</div>,
              },
              {
                key: "open",
                label: "Open",
                children: (
                  <div className="tabContentWrap">
                    {renderVenueCards(openVenues)}
                  </div>
                ),
              },
              {
                key: "closed",
                label: "Closed",
                children: (
                  <div className="tabContentWrap">
                    {renderVenueCards(closedVenues)}
                  </div>
                ),
              },
              {
                key: "draft",
                label: "Draft",
                children: (
                  <div className="tabContentWrap">
                    {renderVenueCards(draftVenues)}
                  </div>
                ),
              },
              {
                key: "deleted",
                label: `Deleted (${softDeletedVenues.length})`,
                children: (
                  <div className="tabContentWrap">
                    {renderDeletedVenueCards(softDeletedVenues)}
                  </div>
                ),
              },
            ]}
          />
        </div>
      </Content>
    </PageLayout>
  );
}
